import {
	ButtonWithLoading,
	ErrorModal,
	MesfModal,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Button, Grid, Snackbar } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import { get } from "lodash-es";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { CatalogBucket } from "../../../../../steel/models/CatalogBucket";
import { useModalStyles } from "../../../../eaf-processes/styles/modalStyles";
import { useEAFDataEntryActions } from "../../../hooks/useEAFDataEntryActions";
import { Charge } from "../../../models/eaf.models";
import { DateTimePickerDE } from "../../form-fields/common/DateTimePickerDE";
import { NumberTextFieldDE } from "../../form-fields/common/NumberTextFieldDE";
import { validateField } from "../../tables/utils/validateMinMax";
import { BucketsSelector } from "./components/BucketsSelector";

type Props = {
	show: boolean;
	onHide: () => void;
	charge: Charge;
	limitDates?: {
		start: Date;
		end: Date | null;
	};
};

export const EditCharge = ({ show, onHide, charge, limitDates }: Props) => {
	const { state } = useUTLSettingsContext();

	const MIN_WEIGHT = get(state.settings, "MAX_WEIGHT", 0);
	const MAX_WEIGHT = get(state.settings, "MIN_WEIGHT", 200000);

	const { saveCharge, getBuckets } = useEAFDataEntryActions();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [open, setOpen] = useState(false);
	const [buckets, setBuckets] = useState<CatalogBucket[]>([]);
	const classes = useModalStyles();

	const {
		getValues,
		handleSubmit,
		setValue,
		control,
		reset,
		formState: { errors },
	} = useForm<Charge>({});

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const onSubmit: SubmitHandler<Charge> = async (data: any) => {
		setIsSubmitLoading(true);
		const resp = await saveCharge({ ...data, ChargeId: charge.ChargeId });
		setIsSubmitLoading(false);
		onHide();
	};

	useEffect(() => {
		if (show) {
			reset();
			(async () => {
				const bucketsFromAPI = await getBuckets();
				setBuckets(bucketsFromAPI);
			})();
			setValue("Timestamp", charge?.Timestamp);
			setValue("Weight", charge?.Weight);
			setValue("BucketId", charge?.BucketId || null);
		}
	}, [show]);

	return (
		<>
			<Grid container>
				<Grid item>
					<MesfModal
						title="EDIT MANUAL CHARGE"
						open={show}
						handleClose={() => onHide()}
						id="edit-charge-modal"
						maxWidth="sm"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
								<Grid container spacing={2}>
									<Grid item xs={12} md={6}>
										<Controller
											name="BucketId"
											control={control}
											render={({ field }) => (
												<BucketsSelector
													value={field.value || null}
													onChange={(newValue) => {
														setValue("BucketId", newValue, {
															shouldValidate: true,
														});
													}}
													onBlur={field.onBlur}
												/>
											)}
										/>
										{errors.BucketId && (
											<span className={classes.errorLabel}>
												{errors.BucketId.message}
											</span>
										)}
									</Grid>

									<Grid item xs={12} md={6}>
										<Controller
											name="Weight"
											control={control}
											defaultValue={charge?.Weight}
											rules={{
												required: "Weight is required",
												validate: (value) =>
													validateField(
														value,
														MIN_WEIGHT,
														MAX_WEIGHT,
														"Weight",
													),
											}}
											render={({ field }) => (
												<NumberTextFieldDE
													{...field}
													value={getValues("Weight")}
													label="Scrap Weight [lbs]"
													onChange={(val) => setValue("Weight", val)}
													onBlur={() => {}}
												/>
											)}
										/>
										{errors.Weight && (
											<span className={classes.errorLabel}>
												{errors.Weight.message}
											</span>
										)}
									</Grid>
									<Grid item xs={12} md={6}>
										<Controller
											name="Timestamp"
											control={control}
											defaultValue={charge?.Timestamp}
											rules={{
												required: "Timestamp is required",
												validate: (value) => {
													if (limitDates?.start && value < limitDates.start) {
														return "Timestamp must be between the start and end of the process";
													} else if (
														limitDates?.end &&
														value > limitDates.end
													) {
														return "Timestamp must be less than the end of the process";
													} else {
														return true;
													}
												},
											}}
											render={({ field }) => (
												<DateTimePickerDE
													{...field}
													format={"HH:mm - MM/DD/YYYY"}
													value={getValues("Timestamp")}
													label="Time"
													onChange={(val) => {
														if (val) {
															setValue("Timestamp", val);
														}
													}}
													onBlur={() => {}}
													error={errors.Timestamp?.message}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid item md={3} xs={12}>
										<Button
											fullWidth
											variant="contained"
											color="default"
											onClick={() => onHide()}
											size="large"
										>
											Cancel
										</Button>
									</Grid>
									<Grid item md={3} xs={12}>
										<ButtonWithLoading
											fullWidth
											isLoading={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
											size="large"
										>
											Save
										</ButtonWithLoading>
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
					<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
						<MuiAlert
							elevation={6}
							variant="filled"
							severity="success"
							onClose={handleClose}
						>
							The charge was edited successfully
						</MuiAlert>
					</Snackbar>
				</Grid>
			</Grid>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Editing Manual Probe"
			/>
		</>
	);
};
